/**
 * Chunk Error Handler
 *
 * Handles "ChunkLoadError" that occurs when users have an old version of the app
 * loaded and try to navigate to a lazy-loaded route after a new deployment.
 *
 * Strategy:
 * 1. Detect chunk load errors (failed dynamic imports)
 * 2. Automatically reload the page (max 2 attempts)
 * 3. If auto-reload fails, show friendly "Update Available" error message
 *
 * Safety Features:
 * - Max 2 reload attempts to prevent infinite loops
 * - Counter resets after 5 minutes (prevents false lockout)
 * - Only reloads on chunk errors, not real application errors
 */

const RELOAD_KEY = 'chunk-reload-count';
const RELOAD_TIMESTAMP_KEY = 'chunk-reload-timestamp';
const MAX_RELOADS = 2;
const RESET_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Check if an error is a chunk load error
 */
export const isChunkError = (error: Error | ErrorEvent): boolean => {
  const errorMessage = error instanceof Error ? error.message : error.message || '';

  const chunkFailedMessage = /Failed to fetch dynamically imported module/i;
  const importFailedMessage = /Importing a module script failed/i;
  const loadingChunkFailed = /Loading chunk [\d]+ failed/i;
  const chunkLoadError = /ChunkLoadError/i;

  return (
    chunkFailedMessage.test(errorMessage) ||
    importFailedMessage.test(errorMessage) ||
    loadingChunkFailed.test(errorMessage) ||
    chunkLoadError.test(errorMessage)
  );
};

/**
 * Get current reload count and check if it's expired
 */
const getReloadCount = (): number => {
  const timestamp = sessionStorage.getItem(RELOAD_TIMESTAMP_KEY);
  const now = Date.now();

  // Reset counter if it's been more than RESET_TIME
  if (timestamp && now - parseInt(timestamp) > RESET_TIME) {
    sessionStorage.removeItem(RELOAD_KEY);
    sessionStorage.removeItem(RELOAD_TIMESTAMP_KEY);
    return 0;
  }

  const count = sessionStorage.getItem(RELOAD_KEY);
  return count ? parseInt(count) : 0;
};

/**
 * Increment reload count and update timestamp
 */
const incrementReloadCount = (): void => {
  const count = getReloadCount();
  sessionStorage.setItem(RELOAD_KEY, (count + 1).toString());
  sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, Date.now().toString());
};

/**
 * Handle chunk load errors
 */
const handleChunkLoadError = (event: ErrorEvent): void => {
  // Check if this is a chunk load error
  if (!isChunkError(event)) {
    return; // Let other error handlers deal with it
  }

  const reloadCount = getReloadCount();

  if (reloadCount < MAX_RELOADS) {
    // Prevent default error handling
    event.preventDefault();

    // Increment counter
    incrementReloadCount();

    // Log in development
    if (import.meta.env.DEV) {
      console.log(`[ChunkErrorHandler] Chunk load error detected. Reloading... (attempt ${reloadCount + 1}/${MAX_RELOADS})`);
    }

    // Reload the page
    window.location.reload();
  } else {
    // Max reloads reached - let ErrorBoundary handle it
    if (import.meta.env.DEV) {
      console.log(`[ChunkErrorHandler] Max reload attempts (${MAX_RELOADS}) reached. Showing error page.`);
    }
    // Don't prevent default - let the error bubble up to ErrorBoundary
  }
};

/**
 * Initialize the chunk error handler
 * Call this once on app startup
 */
export const initChunkErrorHandler = (): void => {
  // Listen for unhandled errors
  window.addEventListener('error', handleChunkLoadError);

  // Listen for unhandled promise rejections (some chunk errors come as rejections)
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (event.reason instanceof Error && isChunkError(event.reason)) {
      const reloadCount = getReloadCount();

      if (reloadCount < MAX_RELOADS) {
        event.preventDefault();
        incrementReloadCount();

        if (import.meta.env.DEV) {
          console.log(`[ChunkErrorHandler] Chunk load error in promise. Reloading... (attempt ${reloadCount + 1}/${MAX_RELOADS})`);
        }

        window.location.reload();
      }
    }
  });

  if (import.meta.env.DEV) {
    console.log('[ChunkErrorHandler] Initialized');
  }
};

/**
 * Get current reload attempt count (useful for debugging)
 */
export const getReloadAttempts = (): number => {
  return getReloadCount();
};

/**
 * Reset reload counter (useful for testing)
 */
export const resetReloadCounter = (): void => {
  sessionStorage.removeItem(RELOAD_KEY);
  sessionStorage.removeItem(RELOAD_TIMESTAMP_KEY);
};
